import React from 'react';
import { RecurrenceType, RecurrenceConfig } from '../../types/automatisation.types';
import './RecurrenceSelector.css';

const MONTHS = [
  { value: 1, label: 'Jan' },
  { value: 2, label: 'Fév' },
  { value: 3, label: 'Mar' },
  { value: 4, label: 'Avr' },
  { value: 5, label: 'Mai' },
  { value: 6, label: 'Jun' },
  { value: 7, label: 'Jul' },
  { value: 8, label: 'Aoû' },
  { value: 9, label: 'Sep' },
  { value: 10, label: 'Oct' },
  { value: 11, label: 'Nov' },
  { value: 12, label: 'Déc' }
];

const ALL_MONTHS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];

interface RecurrenceSelectorProps {
  recurrenceType: RecurrenceType;
  recurrenceConfig: RecurrenceConfig;
  onChange: (type: RecurrenceType, config: RecurrenceConfig) => void;
}

export const RecurrenceSelector: React.FC<RecurrenceSelectorProps> = ({
  recurrenceType,
  recurrenceConfig,
  onChange
}) => {
  const handleTypeChange = (type: RecurrenceType) => {
    let newConfig: RecurrenceConfig = {};
    
    switch (type) {
      case 'hourly':
        newConfig = { interval: 1 };
        break;
      case 'daily':
        newConfig = { hour: 9, minute: 0 };
        break;
      case 'weekly':
        newConfig = { daysOfWeek: [1], hour: 9, minute: 0 };
        break;
      case 'monthly':
        newConfig = { dayOfMonth: 1, monthsOfYear: [...ALL_MONTHS], hour: 9, minute: 0 };
        break;
      default:
        newConfig = {};
    }
    
    onChange(type, newConfig);
  };

  const handleConfigChange = (key: keyof RecurrenceConfig, value: any) => {
    onChange(recurrenceType, { ...recurrenceConfig, [key]: value });
  };

  return (
    <div className="admin-automatisation-recurrence">
      <div className="admin-automatisation-recurrence-type">
        <label className="admin-automatisation-recurrence-label">Type de récurrence</label>
        <select
          value={recurrenceType}
          onChange={(e) => handleTypeChange(e.target.value as RecurrenceType)}
          className="admin-automatisation-recurrence-select"
        >
          <option value="hourly">Horaire</option>
          <option value="daily">Quotidien</option>
          <option value="weekly">Hebdomadaire</option>
          <option value="monthly">Mensuel</option>
        </select>
      </div>

      <div className="admin-automatisation-recurrence-config">
        {recurrenceType === 'hourly' && (
          <div className="admin-automatisation-recurrence-field">
            <label className="admin-automatisation-recurrence-label">
              Intervalle (heures)
            </label>
            <input
              type="number"
              min="1"
              max="24"
              value={recurrenceConfig.interval || 1}
              onChange={(e) => handleConfigChange('interval', parseInt(e.target.value))}
              className="admin-automatisation-recurrence-input"
            />
            <span className="admin-automatisation-recurrence-help">
              Exécution toutes les {recurrenceConfig.interval || 1} heure(s)
            </span>
          </div>
        )}

        {(recurrenceType === 'daily' || recurrenceType === 'weekly' || recurrenceType === 'monthly') && (
          <div className="admin-automatisation-recurrence-time">
            <div className="admin-automatisation-recurrence-field">
              <label className="admin-automatisation-recurrence-label">Heure</label>
              <input
                type="number"
                min="0"
                max="23"
                value={recurrenceConfig.hour || 0}
                onChange={(e) => handleConfigChange('hour', parseInt(e.target.value))}
                className="admin-automatisation-recurrence-input-small"
              />
            </div>
            <span className="admin-automatisation-recurrence-separator">:</span>
            <div className="admin-automatisation-recurrence-field">
              <label className="admin-automatisation-recurrence-label">Minutes</label>
              <input
                type="number"
                min="0"
                max="59"
                value={recurrenceConfig.minute || 0}
                onChange={(e) => handleConfigChange('minute', parseInt(e.target.value))}
                className="admin-automatisation-recurrence-input-small"
              />
            </div>
          </div>
        )}

        {recurrenceType === 'weekly' && (
          <div className="admin-automatisation-recurrence-field">
            <label className="admin-automatisation-recurrence-label">Jours de la semaine</label>
            <div className="admin-automatisation-days-selector">
              {[
                { value: 0, label: 'Dim' },
                { value: 1, label: 'Lun' },
                { value: 2, label: 'Mar' },
                { value: 3, label: 'Mer' },
                { value: 4, label: 'Jeu' },
                { value: 5, label: 'Ven' },
                { value: 6, label: 'Sam' }
              ].map(day => {
                const selectedDays = recurrenceConfig.daysOfWeek || (recurrenceConfig.dayOfWeek ? [recurrenceConfig.dayOfWeek] : [1]);
                const isSelected = selectedDays.includes(day.value);
                return (
                  <label key={day.value} className="admin-automatisation-day-checkbox">
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={(e) => {
                        let newDays = [...selectedDays];
                        if (e.target.checked) {
                          if (!newDays.includes(day.value)) {
                            newDays.push(day.value);
                          }
                        } else {
                          newDays = newDays.filter(d => d !== day.value);
                        }
                        // Keep at least one day selected
                        if (newDays.length > 0) {
                          handleConfigChange('daysOfWeek', newDays.sort());
                          // Clear legacy dayOfWeek field
                          if (recurrenceConfig.dayOfWeek !== undefined) {
                            onChange(recurrenceType, {
                              ...recurrenceConfig,
                              daysOfWeek: newDays.sort(),
                              dayOfWeek: undefined
                            });
                          }
                        }
                      }}
                    />
                    <span className={isSelected ? 'selected' : ''}>{day.label}</span>
                  </label>
                );
              })}
            </div>
            <span className="admin-automatisation-recurrence-help">
              Sélectionnez les jours où l'automatisation doit s'exécuter
            </span>
          </div>
        )}

        {recurrenceType === 'monthly' && (
          <>
            <div className="admin-automatisation-recurrence-field">
              <label className="admin-automatisation-recurrence-label">Jour du mois</label>
              <input
                type="number"
                min="1"
                max="31"
                value={recurrenceConfig.dayOfMonth || 1}
                onChange={(e) => handleConfigChange('dayOfMonth', parseInt(e.target.value))}
                className="admin-automatisation-recurrence-input"
              />
            </div>
            <div className="admin-automatisation-recurrence-field">
              <label className="admin-automatisation-recurrence-label">Mois de l'année</label>
              <div className="admin-automatisation-months-selector">
                {MONTHS.map(month => {
                  const selectedMonths = recurrenceConfig.monthsOfYear || ALL_MONTHS;
                  const isSelected = selectedMonths.includes(month.value);
                  return (
                    <label key={month.value} className="admin-automatisation-month-checkbox">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={(e) => {
                          let newMonths = [...selectedMonths];
                          if (e.target.checked) {
                            if (!newMonths.includes(month.value)) {
                              newMonths.push(month.value);
                            }
                          } else {
                            newMonths = newMonths.filter(m => m !== month.value);
                          }
                          // Keep at least one month selected
                          if (newMonths.length > 0) {
                            handleConfigChange('monthsOfYear', newMonths.sort((a, b) => a - b));
                          }
                        }}
                      />
                      <span className={isSelected ? 'selected' : ''}>{month.label}</span>
                    </label>
                  );
                })}
              </div>
              <span className="admin-automatisation-recurrence-help">
                Sélectionnez les mois où l'automatisation doit s'exécuter
              </span>
            </div>
          </>
        )}
      </div>

      <div className="admin-automatisation-recurrence-preview">
        <strong>Prochaine exécution prévue:</strong>
        <span className="admin-automatisation-recurrence-preview-text">
          {getNextExecutionPreview(recurrenceType, recurrenceConfig)}
        </span>
      </div>
    </div>
  );
};

function getNextExecutionPreview(type: RecurrenceType, config: RecurrenceConfig): string {
  const now = new Date();
  
  switch (type) {
    case 'hourly': {
      const nextHour = new Date(now);
      nextHour.setHours(nextHour.getHours() + (config.interval || 1));
      return `Dans ${config.interval || 1} heure(s) (${nextHour.toLocaleString('fr-BE')})`;
    }

    case 'daily': {
      const tomorrow = new Date(now);
      tomorrow.setDate(tomorrow.getDate() + 1);
      tomorrow.setHours(config.hour || 0);
      tomorrow.setMinutes(config.minute || 0);
      return `Demain à ${config.hour || 0}h${(config.minute || 0).toString().padStart(2, '0')}`;
    }

    case 'weekly': {
      const days = ['dimanche', 'lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi'];
      const selectedDays = config.daysOfWeek || (config.dayOfWeek ? [config.dayOfWeek] : [1]);
      const dayNames = selectedDays.map(d => days[d]).join(', ');
      return `Chaque ${dayNames} à ${config.hour || 0}h${(config.minute || 0).toString().padStart(2, '0')}`;
    }

    case 'monthly': {
      const monthNames = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Jun', 'Jul', 'Aoû', 'Sep', 'Oct', 'Nov', 'Déc'];
      const selectedMonths = config.monthsOfYear || [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];
      const time = `${config.hour || 0}h${(config.minute || 0).toString().padStart(2, '0')}`;

      if (selectedMonths.length === 12) {
        return `Le ${config.dayOfMonth || 1} de chaque mois à ${time}`;
      } else {
        const monthLabels = selectedMonths.map(m => monthNames[m - 1]).join(', ');
        return `Le ${config.dayOfMonth || 1} en ${monthLabels} à ${time}`;
      }
    }

    default:
      return 'Configuration personnalisée';
  }
}